import type { Coordinate } from "ol/coordinate";
import Feature from "ol/Feature";
import LineString from "ol/geom/LineString";
import Point from "ol/geom/Point";
import { type ProjectionLike, transform } from "ol/proj";
import { getAPIBaseUrl, getJwtToken } from "./environment";
import { pairwise } from "./utils";

export const detourLineTypes = ["closed_way_line", "detour_line"] as const;
export type DetourLineType = (typeof detourLineTypes)[number];

export type ClosedWayGeometries = {
  closed_way_line?: Coordinate[];
  closed_way_control_points?: Coordinate[];
  start_coordinates?: Coordinate;
  end_coordinates?: Coordinate;
};

export type DetourGeometries = {
  detour_line?: Coordinate[];
  detour_control_points?: Coordinate[];
};

export type ServerGeometries = ClosedWayGeometries & DetourGeometries;

export type ServerDetour = ServerGeometries & {
  uuid: string;
};

export const partByLineType: Record<DetourLineType, number> = {
  closed_way_line: 0,
  detour_line: 1,
};

const SERVER_PROJECTION = "EPSG:4326";

export async function fetchDetour(uuid: string): Promise<ServerDetour> {
  const response = await fetch(`${getAPIBaseUrl()}/detour/${uuid}`, {
    headers: {
      Authorization: `Bearer ${getJwtToken()}`,
    },
  });
  return await response.json();
}

export function readClosedWayGeometries(
  detour: ServerDetour,
  projection: ProjectionLike,
): Feature<LineString | Point>[] {
  if (!detour.closed_way_line || !detour.closed_way_control_points) {
    return [];
  }
  const features: Feature<LineString | Point>[] = detour.closed_way_control_points.map((point, index) => {
    return new Feature({
      geometry: new Point(point).transform(SERVER_PROJECTION, projection),
      type: "controlPoint",
      part: partByLineType.closed_way_line,
      snapped: true,
      index: index,
    });
  });
  features.at(0)?.set("subtype", "first");
  features.at(-1)?.set("subtype", "last");

  // FIXME: read detour.start_coordinates and detour.end_coordinates

  for (const [previous, current] of pairwise(detour.closed_way_control_points)) {
    const from = coordinateIndex(detour.closed_way_line, previous);
    const to = coordinateIndex(detour.closed_way_line, current);
    const segment = new Feature({
      geometry: new LineString(detour.closed_way_line.slice(from, to + 1)).transform(SERVER_PROJECTION, projection),
      type: "segment",
      part: partByLineType.closed_way_line,
      snapped: true,
    });
    features.push(segment as Feature<LineString>);
  }

  return features;
}

export function readDetourGeometries(detour: ServerDetour, projection: ProjectionLike): Feature<LineString | Point>[] {
  if (!detour.detour_line || !detour.detour_control_points) {
    return [];
  }
  const features: Feature<LineString | Point>[] = detour.detour_control_points.map((point, index) => {
    return new Feature({
      geometry: new Point(point).transform(SERVER_PROJECTION, projection),
      type: "controlPoint",
      part: partByLineType.detour_line,
      snapped: true,
      index: index,
    });
  });
  features.at(0)?.set("subtype", "first");
  features.at(-1)?.set("subtype", "last");

  for (const [previous, current] of pairwise(detour.detour_control_points)) {
    const from = coordinateIndex(detour.detour_line, previous);
    const to = coordinateIndex(detour.detour_line, current);
    const segment = new Feature({
      geometry: new LineString(detour.detour_line.slice(from, to + 1)).transform(SERVER_PROJECTION, projection),
      type: "segment",
      part: partByLineType.detour_line,
      snapped: true,
    });
    features.push(segment as Feature<LineString>);
  }

  return features;
}

export function writeClosedWayGeometries(
  segments: readonly Feature<LineString>[],
  projection: ProjectionLike,
): ClosedWayGeometries {
  if (segments.length === 0) {
    return {
      closed_way_line: undefined,
      closed_way_control_points: undefined,
      start_coordinates: undefined,
      end_coordinates: undefined,
    };
  }

  const segments_coordinates = segments.map((segment) => {
    return segment
      .get("profile")
      .map((c: Coordinate) => c.slice(0, 3))
      .map((c: Coordinate) => transform(c, projection, SERVER_PROJECTION));
  });

  const control_points_coordinates = segments_coordinates.map((coordinates) => coordinates.at(0).slice(0, 2));
  control_points_coordinates.push(segments_coordinates.at(-1)?.at(-1).slice(0, 2));

  const start_coordinates = control_points_coordinates.at(0);
  if (!start_coordinates) {
    throw new Error("No start coordinates");
  }
  const end_coordinates = control_points_coordinates.at(-1);
  if (!end_coordinates) {
    throw new Error("No end coordinates");
  }
  return {
    closed_way_line: segments_coordinates.flat(),
    closed_way_control_points: control_points_coordinates,
    start_coordinates: start_coordinates,
    end_coordinates: end_coordinates,
  };
}

export function writeDetourGeometries(
  segments: readonly Feature<LineString>[],
  projection: ProjectionLike,
): DetourGeometries {
  if (segments.length === 0) {
    return {
      detour_line: undefined,
      detour_control_points: undefined,
    };
  }

  const segments_coordinates = segments.map((segment) => {
    return segment
      .get("profile")
      .map((c: Coordinate) => c.slice(0, 3))
      .map((c: Coordinate) => transform(c, projection, SERVER_PROJECTION));
  });

  const control_points_coordinates = segments_coordinates.map((coordinates) => coordinates.at(0).slice(0, 2));
  control_points_coordinates.push(segments_coordinates.at(-1)?.at(-1).slice(0, 2));

  return {
    detour_line: segments_coordinates.flat(),
    detour_control_points: control_points_coordinates,
  };
}

export async function createDetour(
  geometries: ServerGeometries,
  properties: Record<string, unknown>,
): Promise<ServerDetour> {
  const response = await fetch(`${getAPIBaseUrl()}/detour`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getJwtToken()}`,
    },
    body: JSON.stringify({ ...geometries, ...properties }),
  });
  return await response.json();
}

export async function updateDetour(
  uuid: string,
  geometries: ServerGeometries,
  properties: Record<string, unknown>,
): Promise<ServerDetour> {
  const response = await fetch(`${getAPIBaseUrl()}/detour/${uuid}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getJwtToken()}`,
    },
    body: JSON.stringify({ ...geometries, ...properties }),
  });
  return await response.json();
}

function coordinateIndex(coordinates: Coordinate[], coordinate: Coordinate): number {
  return coordinates.findIndex((c) => c[0] === coordinate[0] && c[1] === coordinate[1]);
}
